import { cn } from '@sparklayer-io/spark-dash-ui/lib/styles';
import { Button, Card, ExternalLink, Heading, Icon, Spinner, Text, Tooltip } from '@ui/themed';
import { t } from 'i18next';
import { INTERCOM_TRIGGER } from '../../../../constants/intercom';
import {
  type BillingDuration,
  type BillingProvider,
  isLive,
  useEnv,
} from '../../../../context/env-context';
import { useHandlers } from '../../../../context/page-context';
import { formatMoney } from '../../../../utils/format-money';
import type { DeepLink } from '../../types';
import { doesBillingProviderApplyTAX, getBillingDurationReadable } from '../utils';

export function PlanDisplay(props: {
  name?: string;
  billingDuration?: BillingDuration;
  billingProvider?: BillingProvider;
  price?: number;
  ordersAllowed?: number;
  salesAgentsAllowed?: number;
  isEnterprise: boolean;
  pending?: boolean;
  currentOrders?: {
    count: number | undefined;
    isLoading: boolean;
  };
  confirmPlan?: string;
  deepLinks?: DeepLink[];
}) {
  const env = useEnv();
  const { navigationHandler, notificationHandler } = useHandlers();

  const salesAgentsAllowed =
    props.salesAgentsAllowed === -1 ? '∞' : (props.salesAgentsAllowed ?? 0);
  const price = formatMoney((props.price ?? 0) / 100, 'usd');

  const billingDurationReadable = getBillingDurationReadable(props.billingDuration);
  const billingProviderAppliesTAX = doesBillingProviderApplyTAX(props.billingProvider);

  return (
    <Card>
      <div className="flex flex-row flex-wrap justify-between">
        <Heading level="h2" style="sm">
          {!props.pending
            ? t('features:account.components.plan_display.title')
            : t('features:account.components.plan_display.title_pending')}
        </Heading>
        {props.deepLinks && props.deepLinks.length > 0 && (
          <div className="flex flex-row flex-wrap gap-1">
            {props.deepLinks.map((link) => (
              <Button
                variant="secondary"
                onClick={() =>
                  navigationHandler.navigate(link.url, { absolute: true, newTab: true })
                }
                key={link.label}
              >
                {link.label}
              </Button>
            ))}
          </div>
        )}
      </div>
      <Card.Body>
        <div className="flex flex-row flex-wrap gap-2">
          <div className="grow">
            <Card variant="secondary" fullHeight>
              <Text variant="secondary">
                {t('features:account.components.plan_display.cards.plan.title')}
              </Text>
              <div className="flex flex-row flex-wrap justify-between gap-1">
                <Text size="lg" bold>
                  <span className="text-platform-primary">{props.name}</span>
                </Text>
                {props.pending && (
                  <Button
                    variant="primary"
                    loading={!props.confirmPlan}
                    onClick={() => {
                      if (props.confirmPlan) {
                        // absolute path will navigate to https://app.env.sparklayer.io/path if no protocol is included
                        navigationHandler.navigate(props.confirmPlan, {
                          absolute: true,
                          excludeSiteEnv: true,
                          excludeSiteId: true,
                        });
                      } else {
                        // Should be unreachable due to the loading prop
                        notificationHandler.error(t('errors:generic'));
                      }
                    }}
                    size="sm"
                  >
                    {t('features:account.components.plan_display.cards.plan.confirm_plan')}
                  </Button>
                )}
              </div>
            </Card>
          </div>
          <div className="grow">
            <Card variant="secondary" fullHeight>
              <Text variant="secondary">
                {billingDurationReadable}
                {!props.pending && billingProviderAppliesTAX && '*'}
              </Text>
              <Text size="lg" bold>
                <span className="text-platform-primary">{price}</span>
              </Text>
            </Card>
          </div>
          <div className="grow">
            <Card variant="secondary" fullHeight>
              <Text variant="secondary">
                {t('features:account.components.plan_display.cards.orders.title')}
                {!props.pending && isLive(env.environment) && !props.isEnterprise && '*'}
              </Text>
              {!props.pending && props.currentOrders ? (
                <div className="flex flex-row gap-1">
                  <Text size="lg" bold>
                    <span className="text-platform-primary">
                      {props.currentOrders.isLoading ? <Spinner /> : props.currentOrders.count}
                    </span>
                  </Text>
                  <div className="flex flex-col justify-center">
                    <Text variant="secondary" bold={false}>
                      {t('features:account.components.plan_display.cards.orders.body', {
                        val: props.ordersAllowed ?? 'N/A',
                      })}
                    </Text>
                  </div>
                </div>
              ) : (
                <Text size="lg" bold>
                  <span className="text-platform-primary">{props.ordersAllowed ?? 'N/A'}</span>
                </Text>
              )}
            </Card>
          </div>
          <div className="grow">
            <Card variant="secondary" fullHeight>
              <Text variant="secondary">
                <ExternalLink link="https://docs.sparklayer.io/sales-agent-ordering" showIcon>
                  {t('features:account.components.plan_display.cards.sales_agents.title')}
                </ExternalLink>
              </Text>
              <div className="flex flex-row gap-1">
                <Text size="lg" bold>
                  <span className="text-platform-primary">{salesAgentsAllowed}</span>
                </Text>
                {!props.pending && (
                  <div className="flex flex-col justify-center">
                    <div className="flex flex-row gap-1">
                      <Text variant="secondary" bold={false}>
                        {t('features:account.components.plan_display.cards.sales_agents.body')}
                      </Text>
                      <Tooltip
                        content={t(
                          'features:account.components.plan_display.cards.sales_agents.tooltip',
                        )}
                      >
                        <span
                          className={cn(
                            'flex cursor-pointer flex-row gap-1 text-platform-primary',
                            INTERCOM_TRIGGER.contact,
                          )}
                        >
                          {t('features:account.components.plan_display.cards.sales_agents.link')}
                          <span className="flex flex-col justify-center">
                            <Icon name="chat" />
                          </span>
                        </span>
                      </Tooltip>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          </div>
        </div>

        {!props.pending && isLive(env.environment) && !props.isEnterprise && (
          <Text variant="secondary" size="sm">
            {t('features:account.components.plan_display.notes.order_count', {
              val: props.ordersAllowed,
            })}
          </Text>
        )}
        {!props.pending && billingProviderAppliesTAX && (
          <Text variant="secondary" size="sm">
            {t('features:account.components.plan_display.notes.tax')}
          </Text>
        )}

        {!props.pending && isLive(env.environment) && props.isEnterprise && (
          <Text variant="secondary">
            {t('features:account.components.plan_display.enterprise_footer.body')}{' '}
            <ExternalLink link="https://docs.sparklayer.io/account-settings#1DwEX" showIcon>
              {t('features:account.components.plan_display.enterprise_footer.link')}
            </ExternalLink>
          </Text>
        )}
      </Card.Body>
    </Card>
  );
}
